import React, { useEffect, useState, useCallback } from "react";
import {
  Card, Row, Col, InputNumber, Input, Button, Table, Space, Typography, message,
  Divider, Select, Popconfirm, Tag, Tooltip, Empty
} from "antd";
import {
  ReloadOutlined, SaveOutlined, PlusOutlined, DeleteOutlined, FileExcelOutlined,
  FilePdfOutlined, SettingOutlined, CheckOutlined
} from "@ant-design/icons";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import dayjs from "dayjs";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../../variables/constants";
import { frenchMonth } from "../../../../variables/functions";

const { Text } = Typography;

const fmt = (v) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(Math.round(Number(v) || 0));

// Rétrocession VT (Véhicules en Transit) — écran dédié : production VT + charges → répartition
// via les clés VT (base). Modèle DIFFÉRENT des cessions classiques.
const VtRetrocession = ({ mois }) => {
  const [input, setInput] = useState(null);
  const [result, setResult] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [computing, setComputing] = useState(false);
  const [validating, setValidating] = useState(false);
  const [nouvelleCharge, setNouvelleCharge] = useState("");

  const chargerCatalogue = useCallback(() => {
    axiosInstance.get("/api/vt-retrocession/charges")
      .then((res) => setCatalogue(Array.isArray(res.data) ? res.data : []))
      .catch(() => { /* silencieux */ });
  }, []);

  const chargerPreview = useCallback(() => {
    if (!mois) return;
    setLoading(true);
    axiosInstance.get(`/api/vt-retrocession/preview/${mois}`)
      .then((res) => {
        setResult(res.data);
        setInput(res.data?.input || null);
      })
      .catch(() => message.error("Impossible de charger la rétrocession VT."))
      .finally(() => setLoading(false));
  }, [mois]);

  useEffect(() => {
    chargerCatalogue();
  }, [chargerCatalogue]);

  useEffect(() => {
    chargerPreview();
  }, [chargerPreview]);

  const setField = (key, value) => setInput((prev) => ({ ...prev, [key]: value }));

  const recalculer = () => {
    if (!input) return;
    setComputing(true);
    axiosInstance.post("/api/vt-retrocession/compute", input)
      .then((res) => { setResult(res.data); setInput(res.data?.input || input); })
      .catch(() => message.error("Échec du calcul."))
      .finally(() => setComputing(false));
  };

  const valider = () => {
    if (!input) return;
    setValidating(true);
    axiosInstance.post("/api/vt-retrocession/validate", input)
      .then((res) => { setResult(res.data); message.success("Rétrocession VT validée et enregistrée."); })
      .catch(() => message.error("Échec de la validation."))
      .finally(() => setValidating(false));
  };

  // ── Charges ──
  const setChargeMontant = (idx, montant) => {
    setInput((prev) => {
      const charges = [...(prev.charges || [])];
      charges[idx] = { ...charges[idx], montant: montant || 0 };
      return { ...prev, charges };
    });
  };
  const retirerCharge = (idx) => {
    setInput((prev) => ({ ...prev, charges: (prev.charges || []).filter((_, i) => i !== idx) }));
  };
  const ajouterChargeDepuisCatalogue = (libelle) => {
    setInput((prev) => {
      if ((prev.charges || []).some((c) => c.libelle === libelle)) return prev;
      return { ...prev, charges: [...(prev.charges || []), { libelle, montant: 0 }] };
    });
  };
  const ajouterAuCatalogue = () => {
    const lib = nouvelleCharge.trim();
    if (!lib) return;
    axiosInstance.post("/api/vt-retrocession/charges", { libelle: lib })
      .then(() => { setNouvelleCharge(""); chargerCatalogue(); ajouterChargeDepuisCatalogue(lib); message.success("Charge ajoutée au catalogue."); })
      .catch((e) => message.error(e?.response?.data?.message || "Charge déjà existante ?"));
  };

  const chargeTotal = (input?.charges || []).reduce((s, c) => s + (Number(c.montant) || 0), 0);

  // ── Colonnes du tableau de rétrocession ──
  const companyCols = [
    { title: "Société d'assurances", dataIndex: "compagnieNom", key: "nom", fixed: "left", width: 190,
      render: (v, r) => r.compagnieCode === "TOTAUX" ? <b>TOTAUX</b> : v },
    { title: "Clé avant 10/2012", key: "ch", width: 110, align: "right",
      render: (_, r) => r.compagnieCode === "TOTAUX" ? "" : `${(r.cleHistorique * 100).toFixed(2)} %` },
    { title: "Clé après 10/2012", key: "cc", width: 110, align: "right",
      render: (_, r) => r.compagnieCode === "TOTAUX" ? "" : `${(r.cleCourante * 100).toFixed(2)} %` },
    { title: "Primes rétrocédées", dataIndex: "primeRetrocedee", key: "e", width: 130, align: "right", render: fmt },
    { title: "Dotation SAP", dataIndex: "dotationSap", key: "f", width: 110, align: "right", render: fmt },
    { title: "Frais admin.", dataIndex: "fraisAdmin", key: "g", width: 110, align: "right", render: fmt },
    { title: "Comm. STAC+Transit.", dataIndex: "commissions", key: "h", width: 140, align: "right", render: fmt },
    { title: "Prévention", dataIndex: "prevention", key: "i", width: 100, align: "right", render: fmt },
    { title: "Sinistres payés Pool", dataIndex: "sinistres", key: "j", width: 130, align: "right", render: fmt },
    { title: "Charges à répartir", dataIndex: "charges", key: "k", width: 120, align: "right", render: fmt },
    { title: "Total à déduire", dataIndex: "totalADeduire", key: "l", width: 120, align: "right",
      render: (v) => <span style={{ color: "#cf1322" }}>{fmt(v)}</span> },
    { title: "SOLDE TECHNIQUE NET", dataIndex: "soldeTechniqueNet", key: "m", width: 150, align: "right", fixed: "right",
      render: (v) => <b style={{ color: "#237804" }}>{fmt(v)}</b> },
  ];

  const dataSource = result ? [...(result.lignes || []), { ...result.totaux, compagnieCode: "TOTAUX" }] : [];

  // ── Exports (reproduction FIDÈLE de la feuille RETRO du classeur) ──
  const moisLabel = mois ? `${frenchMonth(mois.split("-")[1])} ${mois.split("-")[0]}`.toUpperCase() : "";
  const titre = `RETRO VT ${moisLabel}`;
  const pct = (x) => `${(Math.round((Number(x) || 0) * 10000) / 100).toFixed(2)} %`;

  // Bloc supérieur — paramètres & charges (D1 → D14), à l'identique du classeur.
  const blocParametres = () => {
    const p = input, r = result || {};
    const rows = [
      ["NOMBRE DE VEHICULES LEGERS", p.nbVl],
      ["NOMBRE DE CAMIONS", p.nbCamion],
      ["PRIMES NETTES A COMMISSIONER A STAC", Math.round(p.baseStac || 0)],
      ["TAUX DE COMMISSION STAC", pct(p.tauxStac)],
      ["MONTANT DES COMMISSIONS STAC", Math.round(r.commissionStac || 0)],
      ["MONTANT DES COMMISSIONS DES TRANSITAIRES (250 F / Véhicule)", Math.round(r.commissionTransitaire || 0)],
      ["PREVENTION ROUTIERE (250 F / Véhicule)", Math.round(r.preventionRoutiere || 0)],
      ["PRIMES NETTES A REPARTIR", Math.round(p.pnARepartir || 0)],
      ["TAUX DOTATION SAP", pct(p.tauxSap)],
      ["TAUX FRAIS ADMINISTRATIFS", pct(p.tauxFraisAdmin)],
      ["SINISTRES PAYES (survenus après oct 2012)", Math.round(p.sinistresApresOct2012 || 0)],
      ["SINISTRES PAYES (survenus avant oct 2012)", Math.round(p.sinistresAvantOct2012 || 0)],
      ["CHARGE A REPARTIR", Math.round(chargeTotal)],
    ];
    (p.charges || []).forEach((c) => rows.push([`      ${c.libelle}`, Math.round(c.montant || 0)]));
    return rows;
  };

  // Bloc inférieur — tableau de rétrocession (colonnes du classeur : B → M).
  const blocTableau = () => {
    const head = ["SOCIETES D'ASSURANCES", "PRIMES NETTES A REPARTIR",
      "TAUX QUOTE-PART AVANT OCT 2012", "TAUX QUOTE-PART APRES OCT 2012", "PRIMES RETROCEDEES",
      "DOTATION POUR SINISTRES A PAYER", "FRAIS ADMINISTRATIFS", "COMMISSIONS STAC + TRANSITAIRES",
      "PREVENTION ROUTIERE", "SINISTRES PAYES PAR LE POOL", "CHARGES A REPARTIR",
      "TOTAL A DEDUIRE DE LA PRIME RETROCEDEE", "SOLDE TECHNIQUE NET DE RETROCESSION"];
    const rows = dataSource.map((r) => {
      const isT = r.compagnieCode === "TOTAUX";
      return [
        isT ? "TOTAUX" : r.compagnieNom,
        Math.round(input.pnARepartir || 0),
        isT ? "" : pct(r.cleHistorique),
        isT ? "" : pct(r.cleCourante),
        Math.round(r.primeRetrocedee), Math.round(r.dotationSap), Math.round(r.fraisAdmin),
        Math.round(r.commissions), Math.round(r.prevention), Math.round(r.sinistres),
        Math.round(r.charges), Math.round(r.totalADeduire), Math.round(r.soldeTechniqueNet),
      ];
    });
    return { head, rows };
  };

  const exporterExcel = () => {
    const { head, rows } = blocTableau();
    const aoa = [
      ["POOL TPVM-VT GIE"],
      ["Branche : VT"],
      [titre],
      [`Édité le ${dayjs().format("DD/MM/YYYY")} par ${LOGGED_USER?.nom || ""} ${LOGGED_USER?.prenom || ""}`],
      [],
      ...blocParametres(),
      [],
      ["TABLEAU DE RETROCESSION DES OPERATIONS TECHNIQUES PORTANT SUR LES VEHICULES EN TRANSIT (VT)"],
      [`MOIS DE ${moisLabel}`],
      head,
      ...rows,
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = [{ wch: 42 }, ...Array(12).fill({ wch: 15 })];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RETRO");
    XLSX.writeFile(wb, `RETRO_VT_${mois}.xlsx`);
  };

  const exporterPDF = () => {
    const { head, rows } = blocTableau();
    const doc = new jsPDF("landscape", "mm", "a4");
    const W = doc.internal.pageSize.getWidth();
    doc.setFontSize(12); doc.setFont(undefined, "bold");
    doc.text("POOL TPVM-VT GIE — Branche : VT", W / 2, 12, { align: "center" });
    doc.setFontSize(11);
    doc.text(titre, W / 2, 18, { align: "center" });
    // Bloc paramètres (2 colonnes).
    autoTable(doc, {
      body: blocParametres().map((r) => [r[0], typeof r[1] === "number" ? fmt(r[1]) : r[1]]),
      startY: 23, theme: "grid", styles: { fontSize: 7, cellPadding: 1.2 },
      columnStyles: { 0: { cellWidth: 120 }, 1: { cellWidth: 45, halign: "right" } },
      margin: { left: 10 }, tableWidth: 165,
    });
    // Titre du tableau.
    let y = doc.lastAutoTable.finalY + 6;
    doc.setFontSize(9); doc.setFont(undefined, "bold");
    doc.text("TABLEAU DE RETROCESSION DES OPERATIONS TECHNIQUES (VT) — " + moisLabel, W / 2, y, { align: "center" });
    // Tableau de rétrocession.
    autoTable(doc, {
      head: [head], body: rows, startY: y + 3, styles: { fontSize: 6, cellPadding: 1 },
      headStyles: { fillColor: [36, 33, 102], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 5.5, halign: "center" },
      columnStyles: { 0: { cellWidth: 26 } },
      didParseCell: (d) => {
        if (d.section === "body") {
          if (d.column.index > 0) d.cell.styles.halign = "right";
          if (d.row.index === rows.length - 1) d.cell.styles.fontStyle = "bold";
        }
      },
    });
    doc.save(`RETRO_VT_${mois}.pdf`);
  };

  if (!mois) return <Empty description="Sélectionnez un mois." style={{ padding: 40 }} />;

  return (
    <div>
      <Card
        title={<Space><SettingOutlined /> <b>Rétrocession VT — {titre}</b></Space>}
        loading={loading}
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            Modèle spécifique VT (production + charges) — valeurs pré-remplies, ajustables.
          </Text>
        }
        style={{ marginBottom: 16, borderRadius: 12 }}
      >
        {input && (
          <>
            <Row gutter={[16, 12]}>
              <Col xs={12} md={6}>
                <Text type="secondary">Primes nettes à répartir (D8)</Text>
                <InputNumber style={{ width: "100%" }} value={input.pnARepartir}
                  onChange={(v) => setField("pnARepartir", v || 0)} controls={false}
                  formatter={(v) => fmt(v)} parser={(v) => (v || "").replace(/\s/g, "")} />
              </Col>
              <Col xs={12} md={6}>
                <Text type="secondary">Base commissionnable STAC (D3)</Text>
                <InputNumber style={{ width: "100%" }} value={input.baseStac}
                  onChange={(v) => setField("baseStac", v || 0)} controls={false}
                  formatter={(v) => fmt(v)} parser={(v) => (v || "").replace(/\s/g, "")} />
              </Col>
              <Col xs={6} md={3}>
                <Text type="secondary">Nb VL (D1)</Text>
                <InputNumber style={{ width: "100%" }} value={input.nbVl} min={0}
                  onChange={(v) => setField("nbVl", v || 0)} />
              </Col>
              <Col xs={6} md={3}>
                <Text type="secondary">Nb camions (D2)</Text>
                <InputNumber style={{ width: "100%" }} value={input.nbCamion} min={0}
                  onChange={(v) => setField("nbCamion", v || 0)} />
              </Col>
              <Col xs={12} md={6}>
                <Text type="secondary">Sinistres payés APRÈS oct 2012 (D11)</Text>
                <InputNumber style={{ width: "100%" }} value={input.sinistresApresOct2012}
                  onChange={(v) => setField("sinistresApresOct2012", v || 0)} controls={false}
                  formatter={(v) => fmt(v)} parser={(v) => (v || "").replace(/\s/g, "")} />
              </Col>
              <Col xs={12} md={6}>
                <Text type="secondary">Sinistres payés AVANT oct 2012 (D12)</Text>
                <InputNumber style={{ width: "100%" }} value={input.sinistresAvantOct2012}
                  onChange={(v) => setField("sinistresAvantOct2012", v || 0)} controls={false}
                  formatter={(v) => fmt(v)} parser={(v) => (v || "").replace(/\s/g, "")} />
              </Col>
            </Row>

            <Divider style={{ margin: "14px 0" }} orientation="left" plain>
              <Text type="secondary" style={{ fontSize: 12 }}>Taux (paramétrables)</Text>
            </Divider>
            <Row gutter={[16, 12]}>
              <Col xs={8} md={4}>
                <Text type="secondary">Comm. STAC %</Text>
                <InputNumber style={{ width: "100%" }} value={+(input.tauxStac * 100).toFixed(2)} min={0} max={100}
                  onChange={(v) => setField("tauxStac", (v || 0) / 100)} addonAfter="%" />
              </Col>
              <Col xs={8} md={4}>
                <Text type="secondary">Dotation SAP %</Text>
                <InputNumber style={{ width: "100%" }} value={+(input.tauxSap * 100).toFixed(2)} min={0} max={100}
                  onChange={(v) => setField("tauxSap", (v || 0) / 100)} addonAfter="%" />
              </Col>
              <Col xs={8} md={4}>
                <Text type="secondary">Frais admin. %</Text>
                <InputNumber style={{ width: "100%" }} value={+(input.tauxFraisAdmin * 100).toFixed(2)} min={0} max={100}
                  onChange={(v) => setField("tauxFraisAdmin", (v || 0) / 100)} addonAfter="%" />
              </Col>
              <Col xs={8} md={4}>
                <Text type="secondary">Transitaires F/véh</Text>
                <InputNumber style={{ width: "100%" }} value={input.uniteTransitaire} min={0}
                  onChange={(v) => setField("uniteTransitaire", v || 0)} />
              </Col>
              <Col xs={8} md={4}>
                <Text type="secondary">Prévention F/véh</Text>
                <InputNumber style={{ width: "100%" }} value={input.unitePrevention} min={0}
                  onChange={(v) => setField("unitePrevention", v || 0)} />
              </Col>
              <Col xs={8} md={4} style={{ display: "flex", alignItems: "flex-end" }}>
                <Tooltip title="Enregistrer ces taux comme valeurs par défaut">
                  <Button icon={<SaveOutlined />} size="small"
                    onClick={() => axiosInstance.put("/api/vt-retrocession/parametres", {
                      tauxStac: input.tauxStac, tauxSap: input.tauxSap, tauxFraisAdmin: input.tauxFraisAdmin,
                      uniteTransitaire: input.uniteTransitaire, unitePrevention: input.unitePrevention,
                    }).then(() => message.success("Taux par défaut enregistrés.")).catch(() => message.error("Échec."))}>
                    Défauts
                  </Button>
                </Tooltip>
              </Col>
            </Row>

            {result && (
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col><Tag color="blue">Comm. STAC (D5) : {fmt(result.commissionStac)}</Tag></Col>
                <Col><Tag color="blue">Transitaires (D6) : {fmt(result.commissionTransitaire)}</Tag></Col>
                <Col><Tag color="blue">Prévention (D7) : {fmt(result.preventionRoutiere)}</Tag></Col>
              </Row>
            )}

            <Divider style={{ margin: "14px 0" }} orientation="left" plain>
              <Text type="secondary" style={{ fontSize: 12 }}>Charges à répartir (D13 = {fmt(chargeTotal)})</Text>
            </Divider>
            <Table
              size="small" pagination={false} rowKey={(_, i) => i}
              dataSource={input.charges || []}
              columns={[
                { title: "Libellé", dataIndex: "libelle", key: "lib" },
                { title: "Montant", dataIndex: "montant", key: "mnt", width: 200, align: "right",
                  render: (v, _r, idx) => (
                    <InputNumber style={{ width: "100%" }} value={v} controls={false}
                      onChange={(val) => setChargeMontant(idx, val)} formatter={(x) => fmt(x)}
                      parser={(x) => (x || "").replace(/\s/g, "")} />
                  ) },
                { title: "", key: "act", width: 50,
                  render: (_, _r, idx) => (
                    <Button size="small" danger icon={<DeleteOutlined />} onClick={() => retirerCharge(idx)} />
                  ) },
              ]}
              footer={() => (
                <Space wrap>
                  <Select style={{ minWidth: 260 }} placeholder="Ajouter une charge du catalogue"
                    value={null} onChange={ajouterChargeDepuisCatalogue}
                    options={catalogue.map((c) => ({ label: c.libelle, value: c.libelle }))} />
                  <Input style={{ width: 240 }} placeholder="Nouvelle charge (catalogue)"
                    value={nouvelleCharge} onChange={(e) => setNouvelleCharge(e.target.value)}
                    onPressEnter={ajouterAuCatalogue} />
                  <Button icon={<PlusOutlined />} onClick={ajouterAuCatalogue}>Ajouter au catalogue</Button>
                </Space>
              )}
            />

            <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Button type="primary" icon={<ReloadOutlined />} loading={computing} onClick={recalculer}>
                Recalculer
              </Button>
              <Popconfirm title="Valider et enregistrer la rétrocession VT de ce mois ?"
                okText="Valider" cancelText="Annuler" onConfirm={valider}>
                <Button type="primary" danger icon={<CheckOutlined />} loading={validating}>
                  Valider / clôturer
                </Button>
              </Popconfirm>
              <Button icon={<FileExcelOutlined />} onClick={exporterExcel} disabled={!result}>Excel</Button>
              <Button icon={<FilePdfOutlined />} onClick={exporterPDF} disabled={!result}>PDF</Button>
            </div>
          </>
        )}
      </Card>

      <Card title={<b>Tableau de rétrocession VT</b>} style={{ borderRadius: 12 }}>
        <Table
          rowKey={(r) => r.compagnieCode}
          size="small"
          columns={companyCols}
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: 1500 }}
          rowClassName={(r) => (r.compagnieCode === "TOTAUX" ? "vt-total-row" : "")}
        />
      </Card>
    </div>
  );
};

export default VtRetrocession;
